// library
import Spinner from 'common/Spinner'
import { Button as CLButton } from 'component_library/1_Atoms'
import _flatten from 'lodash/flatten'
import { useRouter } from 'next/router'
import { useContext, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
// constants
import { SlotAvailabilityType } from 'app/Bookings/constants'
import {
  dateFormats,
  RudderstackIntegrations,
  sessionStorageKeys,
  TimeOfDay,
} from 'constants/common'
import { RudderEvent } from 'constants/rudderEvents'
import { initialBookingHelperState } from 'state/booking/constants'
import {
  AvailabilityFilterValues,
  LabelAppointmentMapping,
  SLOT_LOCATION_MAP,
} from '../constants'
// datasource
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
// context
import { TimeZoneContext } from 'context/common/TimeZoneContext'
//utils
import { validateAndProceedBooking } from 'app/Bookings/utils'
import { getBookingQueryObject } from 'app/ProviderSearch/utils'
import { getParsedQuery, modifyURLQuery } from 'utils/common'
import logEvent from 'utils/logEvent'
import { isNextOpeningSlotToday } from './utils'
// actions
import {
  cleanBookingInfo,
  startBooking,
  updateBookingDetails,
} from 'state/booking/actions'
// hooks
import useAppSelector from 'hooks/useAppSelector'
import useBreakpoint from 'hooks/useBreakPoint'
import useThriveAuth from 'hooks/useThriveAuth'
// components
import SlotGrid from 'common/SlotGrid'
import SlotGridModal from 'common/SlotGridModal'
import { useDeferredUnmount } from 'hooks/useDeferredUnmount'
import EmptySlots from '../EmptySlots'
import ListCardProviderSection from './ListCardProviderSection'
import MobileListCardProviderSection from './MobileListCardProviderSection'
// css
import { storeBookingDetailsForV2 } from '@app/bookings/utils/bookingHelper'
import { utcToZonedTime } from 'date-fns-tz'
import styles from './index.module.scss'

type ListCardProps = {
  showDistance: boolean
  groupedFilters: GroupedProviderFilter
  slotLocation?: string
  providerCardDetails: {
    dates: (Date | null)[]
    areSlotsEmpty: boolean
    nextAvailabilitySlot: ProviderSlot
    areSlotsLoading: boolean
    error: Error
    count: number
    providerDetails: Provider
    generatedSlots: DateSlot
  }
  renderCustomInsuranceSection?: () => JSX.Element
  /**
   * Time-of-day values that the provider has slots for, but were removed
   * from the search filters. Used to show "bonus" badges in waterfall buckets.
   */
  matchedBonusTimeOfDay?: TimeOfDay[]
}

const ListCard: React.FC<ListCardProps> = ({
  slotLocation,
  providerCardDetails,
  groupedFilters,
  renderCustomInsuranceSection,
  matchedBonusTimeOfDay,
}) => {
  const router = useRouter()
  const {
    isAuthenticated,
    user: userDetails,
    loginWithRedirect,
    isThirdPartyAgent,
  } = useThriveAuth()
  const formattedRouterQuery = getParsedQuery(router)
  const {
    generatedSlots,
    dates,
    error,
    count,
    areSlotsLoading,
    areSlotsEmpty,
    nextAvailabilitySlot,
    providerDetails,
  } = providerCardDetails
  const { id, firstName, lastName, practice } = providerDetails

  //Redux Action
  const dispatch = useDispatch()
  const { isMediumSize, isLargeSize, isSmallSize } = useBreakpoint()

  // ==================================== states and const declaration

  const { dbUser: user, userEmail, b2bUserDetails } = useAppSelector(
    (state) => state.bookingHelper
  )

  const { bookingAttribution, salesforceDetails } = useAppSelector(
    (state) => state.global
  )

  const { timeZone, format } = useContext(TimeZoneContext)

  // ============================================== functions

  const userSelectedAvailType = groupedFilters[
    ProviderFilterTypes.AVAILABILITY_TYPE
  ]?.filter((filter) => {
    if (
      formattedRouterQuery?.availabilityType &&
      filter?.id === formattedRouterQuery?.availabilityType[0]
    ) {
      return filter
    }
  })

  const changeDate = () => {
    const nextDate = utcToZonedTime(nextAvailabilitySlot?.start, timeZone)
    nextDate.setHours(0, 0, 0, 0)
    sessionStorage.setItem(
      sessionStorageKeys.DISABLE_PROVIDER_FETCH_ONCE,
      'true'
    )
    modifyURLQuery(router, {
      slotStartDate: nextDate.getTime().toString(),
    })
  }

  const flattenedCounselingType =
    typeof formattedRouterQuery?.counselingType === 'string'
      ? formattedRouterQuery?.counselingType
      : formattedRouterQuery?.counselingType?.[0]
  const counselingTypes = groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]
  const counselingTypeValue = counselingTypes.find((type) => {
    return type.id === flattenedCounselingType
  })

  const bookAppointment = (
    selectedSlot: SelectedSlot,
    logNextOpeningRudderEvent = false
  ) => {
    //initial appointment mode selection based on user's selection or default to undefined if nothing is selected
    const appointmentMode =
      userSelectedAvailType?.length === 1
        ? userSelectedAvailType[0]?.value === AvailabilityFilterValues.inPerson
          ? SlotAvailabilityType.INPERSON
          : SlotAvailabilityType.ONLINE
        : undefined

    let selectedSlotAppointmentType: SlotAvailabilityType = null

    if (selectedSlot.slot.availabilityType.length === 1) {
      selectedSlotAppointmentType =
        selectedSlot.slot.availabilityType[0] === SlotAvailabilityType.ONLINE
          ? SlotAvailabilityType.ONLINE
          : SlotAvailabilityType.INPERSON
    } else {
      selectedSlotAppointmentType = SlotAvailabilityType.ONLINE //default to video visit if none selected/ both selected
    }

    // ============================================================
    // FIX: Validate if user's selected filter matches the slot's availability
    // This handles waterfall bucket cases where availabilityType filter is removed
    // and the slot may not support the user's originally selected filter
    // ============================================================
    let validatedAppointmentMode = appointmentMode

    if (appointmentMode) {
      // Check if the slot actually supports the user's selected appointment mode
      const slotSupportsSelectedMode = selectedSlot.slot.availabilityType.some(
        (type) => type === appointmentMode
      )

      // If slot doesn't support the user's filter, it's from a waterfall bucket
      // Use the slot's actual availability type instead
      if (!slotSupportsSelectedMode) {
        validatedAppointmentMode = selectedSlotAppointmentType
      }
    }

    const availTypeToAddInRouterUrl = !validatedAppointmentMode
      ? selectedSlotAppointmentType
      : validatedAppointmentMode
    const mappedAvailability = Object?.keys(LabelAppointmentMapping)?.find(
      (availabilityObject) => {
        return (
          LabelAppointmentMapping[availabilityObject] ===
          availTypeToAddInRouterUrl
        )
      }
    )
    const matchedAvailabilityFilter = groupedFilters[
      ProviderFilterTypes.AVAILABILITY_TYPE
    ]?.find((filter) => {
      return filter?.value === mappedAvailability
    })

    dispatch(cleanBookingInfo(isThirdPartyAgent))
    dispatch(
      startBooking({
        ...initialBookingHelperState,
        ...(!!b2bUserDetails && { b2bUserDetails }),
        selectedSlot,
        providerDetails,
        filters: {
          slotAvailabilityTypes: selectedSlot.slot.availabilityType,
        },
        ...(!!user && { dbUser: user, userEmail: user.email }),
        ...(!!userEmail && { userEmail }), //userEmail will be available if redirecting from Support dashboard.
      })
    )
    dispatch(
      updateBookingDetails({
        providerId: providerDetails.id,
        ...(formattedRouterQuery?.counselingType && {
          counselingTypeId: formattedRouterQuery.counselingType[0] as string,
        }),
        counselingType: counselingTypeValue ? counselingTypeValue.value : '',
        // Use validatedAppointmentMode for V1 booking flow
        ...(!validatedAppointmentMode
          ? { appointmentMode: selectedSlotAppointmentType }
          : { appointmentMode: validatedAppointmentMode }),
      })
    )

    // ============================================================
    // FIX: NewBooking flow (V2) - Use matchedAvailabilityFilter.id instead of URL query
    // This ensures waterfall bucket slots get the correct appointmentModeId
    // matchedAvailabilityFilter was already computed with proper validation above
    // ============================================================
    const appointmentModeId = matchedAvailabilityFilter?.id
    const counselingTypeId = formattedRouterQuery?.counselingType?.[0]

    storeBookingDetailsForV2({
      selectedSlot,
      providerDetails,
      appointmentModeId,
      counselingTypeId,
      bookingAttribution,
      salesforceDetails,
    })

    // Always fire PROVIDER_SLOT_SELECTED event for both individual slots and next opening button
    if (!areSlotsEmpty) {
      logEvent(
        RudderEvent.PROVIDER_SLOT_SELECTED,
        {
          provider_name: `${firstName} ${lastName}`,
          provider_id: id,
          practice_state: practice?.state,
          practice_city: practice?.city,
          total_slots_count: count,
          slot_timestamp: selectedSlot.slot?.start,
          account_id: userDetails?.id,
          therapy_type: counselingTypeValue?.value || null,
          ...(logNextOpeningRudderEvent
            ? { slot_location: SLOT_LOCATION_MAP.next_opening }
            : slotLocation && { slot_location: slotLocation }),
        },
        {
          [RudderstackIntegrations.GOOGLE_ADS]: true,
          [RudderstackIntegrations.FACEBOOK_PIXEL]: true,
        }
      )
    }

    const { query } = router

    const queryObject = {
      ...query,
      ...(matchedAvailabilityFilter && {
        availabilityType: matchedAvailabilityFilter.id,
      }),
    }
    validateAndProceedBooking(
      isAuthenticated,
      loginWithRedirect,
      router,
      getBookingQueryObject(queryObject, selectedSlot.slot.id)
    )
  }

  const [showModal, setShowModal] = useState<boolean>(false)

  const { mounted: slotGridMounted, open: slotGridOpen } = useDeferredUnmount(
    showModal
  )

  const handleShowModal = () => {
    setShowModal(true)
  }

  const nextAvailabilitySlotObj = useMemo(() => {
    const firstAvailableDateSlot = _flatten(Object.values(generatedSlots)).find(
      (date) => !!date
    )

    return firstAvailableDateSlot || nextAvailabilitySlot
  }, [generatedSlots])

  return (
    <div className={styles.Card}>
      {slotGridMounted && (
        <SlotGridModal
          showModal={slotGridOpen}
          onHide={() => setShowModal(false)}
          providerDetails={providerDetails}
          userSelectedAvailType={userSelectedAvailType}
          groupedFilters={groupedFilters}
          slotLocation={slotLocation}
        />
      )}
      <div className={styles.Card_subDiv}>
        {isSmallSize ? (
          <MobileListCardProviderSection
            practice={practice}
            providerDetails={providerDetails}
            areSlotsLoading={areSlotsLoading}
            generatedSlots={generatedSlots}
            nextAvailabilitySlot={nextAvailabilitySlot}
            showModal={handleShowModal}
            onNextOpeningClick={bookAppointment}
            renderCustomInsuranceSection={renderCustomInsuranceSection}
            matchedBonusTimeOfDay={matchedBonusTimeOfDay}
            slotLocation={slotLocation}
          />
        ) : (
          <>
            <ListCardProviderSection
              providerDetails={providerDetails}
              practice={practice}
              renderCustomInsuranceSection={renderCustomInsuranceSection}
              matchedBonusTimeOfDay={matchedBonusTimeOfDay}
              slotLocation={slotLocation}
            />
            {isMediumSize || isLargeSize ? (
              <div className={styles.Card_subDiv_nextOpening}>
                {(nextAvailabilitySlotObj || areSlotsLoading) && (
                  <CLButton
                    theme="v2"
                    variant="primary"
                    full
                    aria-label="Select next available opening slot"
                    onClick={() =>
                      bookAppointment(
                        {
                          slot: nextAvailabilitySlotObj,
                          provider: providerDetails,
                        },
                        true
                      )
                    }
                    disabled={areSlotsLoading}
                    className={styles.Card_subDiv_nextOpening_button}
                  >
                    {areSlotsLoading ? (
                      <Spinner />
                    ) : isNextOpeningSlotToday(
                        nextAvailabilitySlotObj,
                        format
                      ) ? (
                      `Next opening: Today, ${format(
                        nextAvailabilitySlotObj?.start,
                        ` ${dateFormats.TIME_FORMAT_LOWER}`
                      ).replace(':00', '')}`
                    ) : (
                      `Next opening: ${format(
                        nextAvailabilitySlotObj?.start,
                        `${dateFormats.DAY_FORMAT} ${dateFormats.MONTH_DATE_SINGLEDIGIT_FORMAT}, ${dateFormats.TIME_FORMAT_LOWER}`
                      ).replace(':00', '')}`
                    )}
                  </CLButton>
                )}
                <button
                  type="button"
                  onClick={handleShowModal}
                  className={`btn btn-link ${styles.view_all_availability_button}`}
                >
                  View all availability
                </button>
              </div>
            ) : (
              <div
                className={`${styles.card_right_container} d-none d-lg-flex flex-column`}
              >
                <div className="flex-lg-grow-1">
                  <SlotGrid
                    dates={dates}
                    showSlotSelection={false}
                    providerDetails={providerDetails}
                    generatedSlots={generatedSlots}
                    showMoreButton
                    onSlotClick={bookAppointment}
                    loadingSlots={areSlotsLoading}
                    errorMessage={error?.message}
                    emptySlots={areSlotsEmpty}
                    emptySlotsComponent={
                      <EmptySlots
                        providerDetails={providerDetails}
                        nextAvailabilityDate={nextAvailabilitySlotObj?.start}
                        changeDate={changeDate}
                      />
                    }
                  />
                </div>
                <button
                  type="button"
                  onClick={handleShowModal}
                  className={`btn btn-link ${styles.view_all_availability_button}`}
                >
                  View all availability
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ListCard
